<template>
  <div class="withdraw-page">
    <div class="withdraw-shell">
      <header class="withdraw-head">
        <button type="button" class="head-btn" :aria-label="$t('auth.back')" @click="back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <div class="head-copy">
          <h1>{{ copy.title }}</h1>
          <p>{{ copy.subtitle }}</p>
        </div>

        <button type="button" class="head-btn" :aria-label="$t('auth.home')" @click="goHome">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 11l9-8 9 8"></path>
            <path d="M5 10v10h14V10"></path>
          </svg>
        </button>
      </header>

      <main class="withdraw-content">
        <section class="status-grid">
          <div class="status-card">
            <span>{{ payType === 2 ? copy.cryptoStatusLabel || copy.bankStatusLabel : copy.bankStatusLabel }}</span>
            <b :class="methodStatusClass">{{ methodStatusText }}</b>
          </div>

          <div class="status-card">
            <span>{{ copy.todayCountLabel }}</span>
            <b>{{ todayWithdrawCount }} / {{ withdrawLimitCount }} {{ copy.countUnit }}</b>
          </div>
        </section>

        <section class="pay-type-card">
          <button
            type="button"
            class="pay-type-btn"
            :class="{ active: payType === 1 }"
            @click="payType = 1"
          >
            {{ copy.payTypeBank || "银行账户" }}
          </button>
          <button
            type="button"
            class="pay-type-btn"
            :class="{ active: payType === 2 }"
            @click="payType = 2"
          >
            {{ copy.payTypeCrypto || "虚拟货币" }}
          </button>
        </section>

        <button
          v-if="!hasRealName"
          type="button"
          class="warn-link"
          @click="$router.push('/Setname')"
        >
          {{ copy.needNameText }}
        </button>

        <button
          v-if="payType === 1 && !hasBank"
          type="button"
          class="warn-link"
          @click="$router.push('/Setbank')"
        >
          {{ copy.needBankText }}
        </button>

        <button
          v-if="payType === 2 && !hasCrypto"
          type="button"
          class="warn-link"
          @click="$router.push('/CryptoBind')"
        >
          {{ copy.needCryptoText || "请先绑定虚拟货币提现地址" }}
        </button>

        <section class="amount-card">
          <div class="field-head">
            <span>{{ copy.amountLabel }}</span>
            <small>{{ minAmountText }}</small>
          </div>

          <div class="amount-entry">
            <b>{{ moneyUnitText }}</b>
            <input
              v-model="withdrawMoney"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              :placeholder="copy.amountPlaceholder"
              @input="handleMoneyInput"
            />
            <button type="button" @click="fillAllMoney">{{ copy.allText }}</button>
          </div>

          <div class="balance-line">
            <span>{{ copy.balanceLabel }}</span>
            <b>{{ balanceText }} {{ moneyUnitText }}</b>
          </div>

          <div v-if="showTodayWithdrawSum" class="balance-line">
            <span>{{ copy.todayTotalLabel }}</span>
            <b>{{ todayWithdrawAmountText }} / {{ todayWithdrawLimitText }} {{ copy.unit }}</b>
          </div>

          <div class="help-link">
            <button type="button" @click="showHelpModal = true">{{ copy.helpText }}</button>
          </div>
        </section>

        <button
          type="button"
          class="submit-btn"
          :disabled="submitting"
          @click="openSubmitModal"
        >
          {{ submitting ? copy.submittingText : copy.submitText }}
        </button>
      </main>
    </div>

    <div v-if="showHelpModal" class="modal-mask" @click.self="showHelpModal = false">
      <div class="modal-card">
        <h3>{{ copy.helpModalTitle }}</h3>
        <p>{{ helpDescription }}</p>
        <button type="button" @click="showHelpModal = false">{{ copy.helpOkText }}</button>
      </div>
    </div>

    <div v-if="showSubmitModal" class="modal-mask" @click.self="closeSubmitModal">
      <div class="modal-card submit-modal">
        <h3>{{ copy.passwordModalTitle }}</h3>
        <p>{{ copy.passwordModalDesc }}</p>
        <input
          v-model="paypassword"
          class="modal-input"
          type="password"
          inputmode="numeric"
          autocomplete="off"
          :placeholder="copy.passwordPlaceholder"
        />
        <button
          type="button"
          :disabled="submitting"
          @click="submitWithdraw"
        >
          {{ submitting ? copy.submittingText : copy.confirmSubmitText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageCopy } from "@/common/pageLocale";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export default {
  data() {
    return {
      submitting: false,
      showHelpModal: false,
      showSubmitModal: false,
      withdrawMoney: "",
      paypassword: "",
      payType: 1,
      userInfo: {},
      bankInfo: null,
      cryptoInfo: null,
      hasCrypto: false,
      withdrawRole: {},
      withdrawList: [],
    };
  },
  computed: {
    copy() {
      return getPageCopy(this, "withdraw");
    },
    ui() {
      const baseInfo = this.$store.getters.getBaseInfo || {};
      return baseInfo.ui || {};
    },
    showTodayWithdrawSum() {
      // Product rule: remove「今日累计」
      return false;
    },
    moneyUnitText() {
      return this.copy.unit || "余额";
    },
    hasRealName() {
      return !!(this.userInfo && String(this.userInfo.name || "").trim());
    },
    hasBank() {
      return !!(this.bankInfo && (this.bankInfo.bankid || this.bankInfo.bankinfo || this.bankInfo.bank_code));
    },
    methodStatusText() {
      if (this.payType === 2) {
        return this.hasCrypto
          ? this.copy.cryptoBound || this.copy.bankApproved
          : this.copy.cryptoUnbound || this.copy.bankUnbound;
      }
      return this.bankStatusText;
    },
    methodStatusClass() {
      if (this.payType === 2) {
        return this.hasCrypto ? "status-ok" : "status-bad";
      }
      return this.bankStatusClass;
    },
    balanceValue() {
      return toNumber(this.userInfo && this.userInfo.money);
    },
    balanceText() {
      return this.formatMoney(this.balanceValue);
    },
    withdrawMin() {
      return toNumber(this.withdrawRole && this.withdrawRole.min);
    },
    withdrawMax() {
      return toNumber(this.withdrawRole && this.withdrawRole.max);
    },
    withdrawLimitCount() {
      return toNumber(this.withdrawRole && this.withdrawRole.num);
    },
    minAmountText() {
      return this.copy.minAmountText.replace("{amount}", this.formatMoney(this.withdrawMin));
    },
    todayKey() {
      const now = new Date();
      const year = now.getFullYear();
      const month = `${now.getMonth() + 1}`.padStart(2, "0");
      const day = `${now.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    todayWithdrawList() {
      return (this.withdrawList || []).filter((item) => {
        const created = String(item.create_time || "");
        return created.slice(0, 10) === this.todayKey;
      });
    },
    todayWithdrawCount() {
      return this.todayWithdrawList.length;
    },
    todayWithdrawAmount() {
      return this.todayWithdrawList.reduce((sum, item) => sum + toNumber(item.money), 0);
    },
    todayWithdrawAmountText() {
      return this.formatMoney(this.todayWithdrawAmount);
    },
    todayWithdrawLimit() {
      const max = this.withdrawMax;
      const count = this.withdrawLimitCount;
      if (!max || !count) {
        return max;
      }
      return max * count;
    },
    todayWithdrawLimitText() {
      return this.formatMoney(this.todayWithdrawLimit);
    },
    bankStatusText() {
      if (!this.hasBank) {
        return this.copy.bankUnbound;
      }
      const status = Number(this.bankInfo && this.bankInfo.status);
      if (status === 1) {
        return this.copy.bankPending;
      }
      if (status === 3) {
        return this.copy.bankRejected;
      }
      return this.copy.bankApproved;
    },
    bankStatusClass() {
      if (!this.hasBank) {
        return "status-bad";
      }
      const status = Number(this.bankInfo && this.bankInfo.status);
      if (status === 1) {
        return "status-wait";
      }
      if (status === 3) {
        return "status-bad";
      }
      return "status-ok";
    },
    helpDescription() {
      return this.dynamicHelpText();
    },
    creditScore() {
      return toNumber(
        (this.userInfo && (this.userInfo.score != null ? this.userInfo.score : this.userInfo.credit_score)) ||
          (this.withdrawRole && this.withdrawRole.user_score)
      );
    },
    minCreditScore() {
      const fromApi = toNumber(
        (this.withdrawRole &&
          (this.withdrawRole.min_score ||
            this.withdrawRole.score ||
            this.withdrawRole.credit ||
            this.withdrawRole.credit_score)) ||
          (this.ui && (this.ui.withdraw_min_score || this.ui.min_credit_score))
      );
      if (fromApi > 0) {
        return fromApi;
      }
      // 后台常见默认：信誉分低于 70 不可提现（角色接口未回传阈值时兜底）
      return this.creditScore > 0 ? 70 : 0;
    },
    isDailyLimitReached() {
      const limit = this.withdrawLimitCount;
      return limit > 0 && this.todayWithdrawCount >= limit;
    },
    isCreditScoreBlocked() {
      const min = this.minCreditScore;
      return min > 0 && this.creditScore > 0 && this.creditScore < min;
    },
  },
  created() {
    if (!localStorage.getItem("token")) {
      this.$toast(this.copy.loginFirst);
      this.$router.replace("/Login");
      return;
    }
    this.bootstrap();
  },
  mounted() {
    document.title = `${this.copy.title} · OMN`;
  },
  methods: {
    async bootstrap() {
      await Promise.all([
        this.getUserInfo(),
        this.getUserWithdrawRole(),
        this.getUserBankInfo(),
        this.getUserWithdrawList(),
      ]);
    },
    async getUserInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_info",
      });
      if (res && res.code === 200) {
        this.userInfo = res.data || {};
      }
    },
    async getUserWithdrawRole() {
      const res = await this.$http({
        method: "get",
        url: "user_get_withdraw_role",
      });
      if (res && res.code === 200) {
        this.withdrawRole = res.data || {};
      }
    },
    async getUserBankInfo() {
      const res = await this.$http({
        method: "get",
        url: "user_get_bank",
      });
      if (res && res.code === 200 && res.data) {
        this.bankInfo = res.data.is_bank ? res.data.info || null : null;
        const crypto = res.data.crypto || {};
        this.hasCrypto = !!crypto.is_bind;
        this.cryptoInfo = crypto.info || null;
        if (this.hasCrypto && !this.hasBank) {
          this.payType = 2;
        }
      } else {
        this.bankInfo = null;
        this.hasCrypto = false;
        this.cryptoInfo = null;
      }
    },
    async getUserWithdrawList() {
      const res = await this.$http({
        method: "get",
        url: "user_get_withdraw_list",
      });
      if (res && res.code === 200) {
        this.withdrawList = Array.isArray(res.data) ? res.data : [];
      }
    },
    formatMoney(value) {
      return toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    dynamicHelpText() {
      const count = this.withdrawLimitCount;
      const total = this.formatMoney(this.todayWithdrawLimit);
      return (this.copy.helpDescTemplate || "")
        .replace("{count}", count)
        .replace("{total}", total)
        .replace("{unit}", this.moneyUnitText || "");
    },
    handleMoneyInput(event) {
      const raw = (event && event.target && event.target.value) || this.withdrawMoney;
      const cleaned = raw.replace(/[^\d.]/g, "");
      const parts = cleaned.split(".");
      const integer = parts.shift() || "";
      const decimal = parts.join("").slice(0, 2);
      this.withdrawMoney = decimal ? `${integer}.${decimal}` : integer;
    },
    fillAllMoney() {
      this.withdrawMoney = this.balanceValue ? String(this.balanceValue) : "";
    },
    back() {
      window.history.back();
    },
    goHome() {
      this.$router.push("/Home");
    },
    openSubmitModal() {
      if (!this.hasRealName) {
        this.$toast(this.copy.needNameText);
        this.$router.push("/Setname");
        return;
      }
      if (this.payType === 1 && !this.hasBank) {
        this.$toast(this.copy.needBankText);
        this.$router.push("/Setbank");
        return;
      }
      if (this.payType === 2 && !this.hasCrypto) {
        this.$toast(this.copy.needCryptoText || "请先绑定虚拟货币提现地址");
        this.$router.push("/CryptoBind");
        return;
      }
      if (this.isCreditScoreBlocked()) {
        this.$toast(this.copy.creditScoreError || "信誉分不足，请联系客服");
        return;
      }
      if (this.isDailyLimitReached()) {
        this.$toast(this.copy.withdrawLimitError || "提现次数已达上限，请联系客服");
        return;
      }
      const amount = Number(this.withdrawMoney);
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$toast(this.copy.moneyError);
        return;
      }
      if (!this.userInfo.paypassword) {
        this.$toast(this.copy.noPayPassword);
        this.$router.push("/SetPayPassword");
        return;
      }
      this.showSubmitModal = true;
    },
    closeSubmitModal() {
      if (this.submitting) {
        return;
      }
      this.showSubmitModal = false;
    },
    isPayPasswordError(res) {
      if (!res) {
        return false;
      }
      const msg = String(res.msg || "");
      // 不要把所有业务 401 都当成提现密码错误（信誉分/次数也会返 401）
      return /鉴权|密码错误|支付密码|提现密码|pay\s*password|password/i.test(msg);
    },
    isCreditScoreError(res) {
      const msg = String((res && res.msg) || "");
      const key = String((res && res.msg_key) || "");
      return /信誉|信用分|信誉分|credit.?score|score.?low|min.?score/i.test(`${msg} ${key}`);
    },
    isWithdrawLimitError(res) {
      const msg = String((res && res.msg) || "");
      const key = String((res && res.msg_key) || "");
      return /次数|上限|超限|daily.?limit|withdraw.?limit|too.?many/i.test(`${msg} ${key}`);
    },
    resolveWithdrawError(res) {
      if (this.isCreditScoreError(res)) {
        return this.copy.creditScoreError || "信誉分不足，请联系客服";
      }
      if (this.isWithdrawLimitError(res)) {
        return this.copy.withdrawLimitError || "提现次数已达上限，请联系客服";
      }
      if (this.isPayPasswordError(res)) {
        return this.copy.passwordWrong || "提现密码错误";
      }
      return (res && res.msg) || this.copy.moneyError;
    },
    async submitWithdraw() {
      if (this.submitting) {
        return;
      }
      if (this.isCreditScoreBlocked()) {
        this.$toast(this.copy.creditScoreError || "信誉分不足，请联系客服");
        return;
      }
      if (this.isDailyLimitReached()) {
        this.$toast(this.copy.withdrawLimitError || "提现次数已达上限，请联系客服");
        return;
      }
      const amount = Number(this.withdrawMoney);
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$toast(this.copy.moneyError);
        return;
      }
      if (!this.paypassword) {
        this.$toast(this.copy.passwordError);
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$http({
          method: "post",
          url: "user_set_withdraw",
          data: {
            money: amount.toFixed(2),
            paypassword: this.paypassword,
            pay_type: this.payType === 2 ? 2 : 1,
          },
        });
        if (res && res.code === 200) {
          this.$toast(this.copy.submitSuccess || "提交成功");
          this.paypassword = "";
          this.showSubmitModal = false;
          this.withdrawMoney = "";
          await Promise.all([
            this.getUserInfo(),
            this.getUserWithdrawRole(),
            this.getUserWithdrawList(),
          ]);
          return;
        }
        this.$toast(this.resolveWithdrawError(res));
      } catch (error) {
        this.$toast(this.copy.moneyError);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.withdraw-page {
  min-height: 100vh;
  min-height: 100dvh;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 63, 120, 0.11), transparent 28%),
    radial-gradient(circle at 0 14%, rgba(165, 92, 255, 0.1), transparent 26%),
    #0a0710;
  color: #fff7fb;
}

.withdraw-shell {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  max-width: none;
  margin: 0;
}

.withdraw-head {
  position: sticky;
  top: 0;
  z-index: 20;
  display: grid;
  grid-template-columns: 2.625rem 1fr 2.625rem;
  align-items: center;
  min-height: 4.1875rem;
  padding: max(0.5rem, env(safe-area-inset-top)) 0.8125rem 0.5rem;
  background: rgba(10, 7, 16, 0.92);
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
  backdrop-filter: blur(1.125rem);
  -webkit-backdrop-filter: blur(1.125rem);
}

.head-btn {
  width: 2.375rem;
  height: 2.375rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.045);
  color: #fff;
}

.head-btn svg {
  width: 1.1875rem;
  height: 1.1875rem;
}

.head-copy h1 {
  margin: 0;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 900;
  letter-spacing: -0.0125rem;
}

.head-copy p {
  margin: 0.1875rem 0 0;
  text-align: center;
  color: #a89baa;
  font-size: 0.5625rem;
  line-height: 1.4;
}

.withdraw-content {
  padding: 1rem;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5625rem;
  margin-bottom: 0.875rem;
}

.pay-type-card {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.875rem;
}

.pay-type-btn {
  min-height: 2.5rem;
  border-radius: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #d8c9d8;
  font-size: 0.8125rem;
  font-weight: 700;
}

.pay-type-btn.active {
  border-color: rgba(255, 99, 146, 0.55);
  background: linear-gradient(135deg, rgba(255, 63, 120, 0.22), rgba(165, 92, 255, 0.16));
  color: #fff7fb;
}

.status-card {
  border-radius: 1.125rem;
  padding: 0.8125rem;
  background: linear-gradient(160deg, rgba(72, 36, 62, 0.5), rgba(28, 16, 36, 0.88));
  border: 1px solid rgba(255, 255, 255, 0.085);
}

.status-card span {
  display: block;
  margin-bottom: 0.3125rem;
  color: #a89baa;
  font-size: 0.5625rem;
}

.status-card b {
  font-size: 1rem;
  font-weight: 900;
}

.status-ok {
  color: #4de3a5;
}

.status-wait {
  color: #ffc65c;
}

.status-bad {
  color: #ff4d7d;
}

.warn-link {
  display: block;
  width: 100%;
  margin-bottom: 0.75rem;
  padding: 0.8125rem;
  border: 1px solid rgba(255, 63, 120, 0.2);
  border-radius: 0.9375rem;
  background: rgba(255, 63, 120, 0.1);
  color: #ff91b0;
  font-size: 0.6875rem;
  line-height: 1.55;
  text-align: left;
}

.amount-card {
  padding: 1rem;
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.085);
  box-shadow: 0 0.8125rem 2.125rem rgba(0, 0, 0, 0.15);
}

.field-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.4375rem;
  color: #d5c8d2;
  font-size: 0.6875rem;
  font-weight: 800;
}

.field-head small {
  color: #a89baa;
  font-size: 0.5625rem;
  font-weight: 600;
}

.amount-entry {
  display: flex;
  align-items: center;
  gap: 0.5625rem;
  height: 4.0625rem;
  margin-bottom: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.085);
}

.amount-entry b {
  font-size: 0.9375rem;
  font-weight: 800;
}

.amount-entry input {
  min-width: 0;
  flex: 1;
  padding: 0;
  border: 0;
  background: transparent;
  color: #d8d0d6;
  font-size: 1.75rem;
  line-height: 1;
  font-weight: 900;
  letter-spacing: -0.025rem;
  outline: none;
}

.amount-entry input::placeholder {
  color: #8a8188;
}

.amount-entry button {
  border: 0;
  padding: 0;
  background: none;
  color: #ff7ea5;
  font-size: 0.8125rem;
  font-weight: 900;
}

.balance-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  margin: 0.625rem 0;
  color: #a89baa;
  font-size: 0.6875rem;
}

.balance-line b {
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 800;
  text-align: right;
}

.help-link {
  display: flex;
  justify-content: flex-end;
  margin: 0 0 0.125rem;
}

.help-link button {
  border: 0;
  padding: 0;
  background: transparent;
  color: #c99dff;
  font-size: 0.6875rem;
}

.submit-btn {
  width: 100%;
  height: 3.125rem;
  margin-top: 0.875rem;
  border: 0;
  border-radius: 1.5625rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  box-shadow: 0 0.6875rem 1.625rem rgba(255, 63, 120, 0.24);
  color: #fff;
  font-size: 1.0625rem;
  font-weight: 900;
}

.submit-btn[disabled] {
  opacity: 0.6;
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  background: rgba(3, 2, 5, 0.76);
  backdrop-filter: blur(0.5625rem);
}

.modal-card {
  width: 100%;
  max-width: 24.375rem;
  border-radius: 1.5rem;
  padding: 1.25rem;
  background: #1a1221;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 1.375rem 4.375rem rgba(0, 0, 0, 0.5);
}

.modal-card h3 {
  margin: 0 0 0.625rem;
  font-size: 1.125rem;
  font-weight: 900;
}

.modal-card p {
  margin: 0;
  color: #c9bbc6;
  font-size: 0.75rem;
  line-height: 1.75;
}

.modal-card button {
  width: 100%;
  height: 2.6875rem;
  margin-top: 1rem;
  border: 0;
  border-radius: 0.9375rem;
  background: linear-gradient(135deg, #ff6d9c, #ff3f78);
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 900;
}

.modal-input {
  width: 100%;
  height: 2.875rem;
  margin-top: 0.9375rem;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 0.9375rem;
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  padding: 0 0.875rem;
  outline: none;
}

.modal-input::placeholder {
  color: #776b76;
}

.submit-modal button[disabled] {
  opacity: 0.6;
}

button {
  cursor: pointer;
}

@media (min-width: 48rem) {
  .withdraw-shell {
    max-width: 25rem;
    margin: 0 auto;
  }
}
</style>
